/* ============================================================
   Lavila Reviews — render Google Business Profile reviews from
   our own public API. Vanilla JS, no dependencies, deferred.

   Settings are read from data-* attributes on the section root
   and re-applied live on the Shopify theme-editor events
   `shopify:section:load` and `shopify:section:select`.
   ============================================================ */
(function () {
  'use strict';

  /* ----------------------------------------------------------
     CONFIG
     ----------------------------------------------------------
     GOOGLE_REVIEW_URL — destination of the "Dejar una reseña"
     button (the store's Google "write a review" short link).
     Set it to an empty string ('') to hide the button.
     ---------------------------------------------------------- */
  var GOOGLE_REVIEW_URL = 'https://g.page/r/CUFIKp14h4tiEBM/review';
  /* Store's Google Place ID (ChIJ…). The footer line links to the "see all
     reviews" page (search.google.com/local/reviews?placeid=…); the CTA button
     keeps GOOGLE_REVIEW_URL (write a review). Blank → footer falls back to
     GOOGLE_REVIEW_URL. */
  var GOOGLE_PLACE_ID = 'ChIJvRb3KEmTlxIRQUgqnXiHi2I';
  var API_URL = 'https://lavila-reviews.vercel.app/api/reviews';

  /* Inline Google "G" (4 colours) — no external request. */
  var GOOGLE_G =
    '<svg class="lr-g" viewBox="0 0 48 48" width="1em" height="1em" aria-hidden="true" focusable="false">' +
    '<path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>' +
    '<path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>' +
    '<path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>' +
    '<path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>';

  /* Star glyph from the design reference. */
  var STAR_PATH =
    'M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z';
  /* Chevrons carry explicit width/height so they always render, even if a
     theme global `svg{}` rule interferes (this was the broken-arrow cause). */
  var CHEVRON_LEFT =
    '<svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M15 18l-6-6 6-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var CHEVRON_RIGHT =
    '<svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9 18l6-6-6-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  /* "stacked frames" icon for the +N media count pill. */
  var MEDIA_ICON =
    '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true" focusable="false"><rect x="3" y="3" width="14" height="14" rx="2"/><path d="M21 8v11a2 2 0 0 1-2 2H8"/></svg>';

  /* ----------------------------------------------------------
     Pure helpers (also exported for tests)
     ---------------------------------------------------------- */

  function esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function isHttpUrl(u) {
    return typeof u === 'string' && /^https?:\/\//i.test(u);
  }

  function hasText(r) {
    return r && typeof r.comment_clean === 'string' && r.comment_clean.trim().length > 0;
  }

  function hasMedia(r) {
    return r && Array.isArray(r.media) && r.media.filter(isHttpUrl).length > 0;
  }

  /* Parse the "hide specific reviews" textarea: reviewIds separated by commas,
     newlines or whitespace. Returns a de-duped array. */
  function parseHiddenIds(raw) {
    if (!raw) return [];
    var seen = {};
    var out = [];
    String(raw).split(/[\s,]+/).forEach(function (id) {
      var v = id.trim();
      if (v && !seen[v]) { seen[v] = true; out.push(v); }
    });
    return out;
  }

  /* "Marta Mestres Lladó" -> "Marta M." — first name + the FIRST surname's
     initial only (one initial). ALL-CAPS names are normalized: first name to
     Title Case, the initial stays uppercase ("MARIA MARTINEZ" -> "Maria M.").
     Mixed-case names keep their case; single-word names pass through (also
     caps-normalized, e.g. "Chilax" -> "Chilax"). */
  function formatAuthor(name) {
    var n = String(name == null ? '' : name).trim().replace(/\s+/g, ' ');
    if (!n) return '';
    /* entirely uppercase AND has at least one cased letter */
    var allCaps = n === n.toUpperCase() && n !== n.toLowerCase();
    var parts = n.split(' ');
    var first = parts[0];
    if (allCaps) {
      first = first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
    }
    if (parts.length === 1) return first;
    var initial = parts[1].charAt(0).toUpperCase() + '.';
    return first + ' ' + initial;
  }

  function timeOf(r) {
    var t = r && r.createTime ? new Date(r.createTime).getTime() : 0;
    return isNaN(t) ? 0 : t;
  }

  /* Spanish (es-ES) relative time formatter — no library. */
  function relativeTimeEs(iso, nowMs) {
    var then = new Date(iso).getTime();
    if (isNaN(then)) return '';
    var now = typeof nowMs === 'number' ? nowMs : Date.now();
    var diff = Math.max(0, now - then);
    var sec = Math.floor(diff / 1000);
    if (sec < 60) return 'hace un momento';
    var min = Math.floor(sec / 60);
    if (min < 60) return min === 1 ? 'hace 1 minuto' : 'hace ' + min + ' minutos';
    var hr = Math.floor(min / 60);
    if (hr < 24) return hr === 1 ? 'hace 1 hora' : 'hace ' + hr + ' horas';
    var day = Math.floor(hr / 24);
    if (day < 7) return day === 1 ? 'hace 1 día' : 'hace ' + day + ' días';
    if (day < 30) {
      var wk = Math.max(1, Math.floor(day / 7));
      return wk === 1 ? 'hace 1 semana' : 'hace ' + wk + ' semanas';
    }
    if (day < 365) {
      var mo = Math.max(1, Math.floor(day / 30));
      return mo === 1 ? 'hace 1 mes' : 'hace ' + mo + ' meses';
    }
    var yr = Math.max(1, Math.floor(day / 365));
    return yr === 1 ? 'hace 1 año' : 'hace ' + yr + ' años';
  }

  /* Filter + sort + slice. Pure: takes data + settings (+ optional now for
     tests), returns array. Order: text-only -> rating -> hide-list -> max-age
     -> sort -> slice. */
  function selectReviews(data, settings, nowMs) {
    var list = data && Array.isArray(data.reviews) ? data.reviews.slice() : [];

    /* Text-less reviews are ALWAYS excluded. */
    list = list.filter(hasText);

    /* Minimum rating. 'all' keeps every text review. */
    if (settings.minRating === '5') {
      list = list.filter(function (r) { return Number(r.rating) === 5; });
    } else if (settings.minRating === '4') {
      list = list.filter(function (r) { return Number(r.rating) >= 4; });
    }

    /* Hide-list: exclude specific reviewIds, applied AFTER the rating filter. */
    var hidden = settings.hiddenIds instanceof Set
      ? settings.hiddenIds
      : new Set(settings.hiddenIds || []);
    if (hidden.size) {
      list = list.filter(function (r) { return !hidden.has(r.reviewId); });
    }

    /* Max age: drop reviews older than N days (default 365), applied AFTER the
       rating filter and hide-list. Reviews whose date is unknown/unparseable
       (timeOf === 0) are KEPT, so a single bad timestamp can never collapse the
       whole pool and hide the section. */
    var maxAgeDays = settings.maxAgeDays > 0 ? settings.maxAgeDays : 365;
    var now = typeof nowMs === 'number' ? nowMs : Date.now();
    var cutoff = now - maxAgeDays * 86400000;
    list = list.filter(function (r) {
      var t = timeOf(r);
      return t === 0 || t >= cutoff;
    });

    /* Sort: photos first, then newest within each group. Text reviews follow
       the photo reviews, also newest-first. */
    if (settings.photosFirst) {
      list.sort(function (a, b) {
        var am = hasMedia(a) ? 1 : 0;
        var bm = hasMedia(b) ? 1 : 0;
        if (am !== bm) return bm - am;          // photo reviews first
        return timeOf(b) - timeOf(a);            // then newest first
      });
    } else {
      list.sort(function (a, b) { return timeOf(b) - timeOf(a); });
    }

    var max = settings.maxReviews > 0 ? settings.maxReviews : 12;
    return list.slice(0, max);
  }

  /* Number of filled stars to draw (clamped 0..5). */
  function filledStars(rating) {
    var n = Math.round(Number(rating) || 0);
    return Math.max(0, Math.min(5, n));
  }

  function starsMarkup(rating, label) {
    var full = filledStars(rating);
    var out = '<span class="lr-stars" role="img" aria-label="' + esc(label) + '">';
    for (var i = 1; i <= 5; i++) {
      var cls = i <= full ? 'lr-star lr-star--full' : 'lr-star lr-star--empty';
      out +=
        '<svg class="' + cls + '" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
        '<path d="' + STAR_PATH + '"/></svg>';
    }
    return out + '</span>';
  }

  /* ----------------------------------------------------------
     DOM-dependent code below
     ---------------------------------------------------------- */

  function prefersReducedMotion() {
    return (
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function isDesignMode() {
    return typeof window !== 'undefined' && window.Shopify && window.Shopify.designMode;
  }

  /* Shared data cache. Held on a GLOBAL object (window) rather than a module
     variable so it survives the section <script> being re-inserted and
     RE-EXECUTED by the Shopify theme editor on every setting change. With a
     module-level cache, each re-render reset it to null and re-fetched the API;
     a slow or throttled editor fetch then left the new section stuck on its
     skeleton ("section disappears until save + reload"). A window-scoped cache
     is fetched once and reused instantly on every re-render. */
  var GLOBAL = (typeof window !== 'undefined')
    ? window
    : (typeof globalThis !== 'undefined' ? globalThis : {});
  var STORE = (GLOBAL.__lavilaReviews = GLOBAL.__lavilaReviews || { data: null, inflight: null });

  function getData() {
    if (STORE.data) return Promise.resolve(STORE.data);
    if (STORE.inflight) return STORE.inflight;
    STORE.inflight = fetch(API_URL, { headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (json) {
        STORE.data = json;
        STORE.inflight = null;
        return json;
      })
      .catch(function (err) {
        STORE.inflight = null;   // allow a later retry (e.g. on editor re-render)
        throw err;
      });
    return STORE.inflight;
  }

  function readSettings(root) {
    var num = function (attr, fallback) {
      var v = parseInt(root.getAttribute(attr), 10);
      return isNaN(v) ? fallback : v;
    };
    return {
      heading: root.getAttribute('data-heading') || '',
      minRating: root.getAttribute('data-min-rating') || '4',
      photosFirst: root.getAttribute('data-photos-first') === 'true',
      showReplies: root.getAttribute('data-show-replies') === 'true',
      maxReviews: num('data-max-reviews', 12),
      minToShow: num('data-min-to-show', 4),
      maxAgeDays: num('data-max-age-days', 365),
      autoplay: root.getAttribute('data-autoplay') === 'true',
      autoplaySpeed: Math.max(2, num('data-autoplay-speed', 5)) * 1000,
      hiddenIds: parseHiddenIds(root.getAttribute('data-hidden-ids')),
      showVerified: root.getAttribute('data-show-verified') !== 'false',
      showReadMore: root.getAttribute('data-show-read-more') === 'true',
      showCta: root.getAttribute('data-show-cta') !== 'false',
      showScore: root.getAttribute('data-show-score') !== 'false',
      showFooterSentence: root.getAttribute('data-show-footer-sentence') !== 'false',
      logoUrl: root.getAttribute('data-logo-url') || '',
      logoWidth: num('data-logo-width', 150)
    };
  }

  function sectionWrapper(root) {
    return root.closest('.lavila-reviews') || root;
  }

  function cardMarkup(review, settings, index) {
    var media = (review.media || []).filter(isHttpUrl);
    var hasMedia = media.length > 0;
    var html = '<article class="lr-card" data-has-media="' + (hasMedia ? 'true' : 'false') + '">';

    /* Left photo panel (stacks to the top on mobile via CSS). */
    if (hasMedia) {
      html +=
        '<button type="button" class="lr-card__media" data-lr-lightbox data-lr-index="' + index + '" ' +
        'aria-label="Ampliar fotos de la reseña de ' + esc(review.author) + '">' +
        '<img class="lr-card__media-img" src="' + esc(media[0]) + '" alt="Foto de la reseña de ' +
        esc(review.author) + '" loading="lazy" decoding="async">' +
        (media.length > 1
          ? '<span class="lr-card__media-count" aria-hidden="true">' + MEDIA_ICON + '</span>'
          : '') +
        '</button>';
    }

    /* Content = two groups distributed by space-between (CSS):
         TOP    = stars row + comment text (+ Leer más / reply)
         BOTTOM = name + date on one line. */
    html += '<div class="lr-card__content">';

    html += '<div class="lr-card__top">';
    /* stars (left) + verified pill (right) */
    html += '<div class="lr-card__head">';
    html += starsMarkup(review.rating, filledStars(review.rating) + ' de 5 estrellas');
    if (settings.showVerified) {
      html += '<span class="lr-badge">' + GOOGLE_G + 'Verificada</span>';
    }
    html += '</div>';
    /* comment sits with the stars in the TOP group */
    html += '<div class="lr-card__comment" data-lr-comment>';
    html += '<p class="lr-card__text">' + esc(review.comment_clean) + '</p>';
    html += '</div>';
    if (settings.showReadMore) {
      html += '<button type="button" class="lr-card__more" data-lr-more hidden>Leer más</button>';
    }
    if (settings.showReplies && review.reply && String(review.reply).trim()) {
      html += '<div class="lr-card__reply">';
      html += '<span class="lr-card__reply-label">Respuesta de Lavila Home</span>';
      html += '<p class="lr-card__reply-text">' + esc(review.reply) + '</p>';
      html += '</div>';
    }
    html += '</div>';   /* end .lr-card__top */

    /* BOTTOM group: name + date on one line, comma-separated. */
    html += '<div class="lr-card__footer">';
    html += '<span class="lr-card__byline">';
    html += '<span class="lr-card__author">' + esc(formatAuthor(review.author)) + '</span>, ';
    html += '<span class="lr-card__date">' + esc(relativeTimeEs(review.createTime)) + '</span>';
    html += '</span></div>';

    html += '</div></article>';
    return html;
  }

  /* Top of the section: just the title. */
  function headingMarkup(settings) {
    return (
      '<header class="lr-aggregate">' +
      '<h2 class="lr-aggregate__heading">' + esc(settings.heading) + '</h2>' +
      '</header>'
    );
  }

  /* Multicolour "Google" wordmark — brand letter colours in the theme font
     (restrained). role=img + aria-label so SR reads "Google", not letters. */
  function googleWordmark() {
    var colors = ['#4285F4', '#EA4335', '#FBBC05', '#4285F4', '#34A853', '#EA4335'];
    var word = 'Google';
    var out = '<span class="lr-google" role="img" aria-label="Google">';
    for (var i = 0; i < word.length; i++) {
      out += '<span aria-hidden="true" style="color:' + colors[i] + '">' + word.charAt(i) + '</span>';
    }
    return out + '</span>';
  }

  /* Prominent aggregate score block at the TOP (lead trust signal): big live
     average + gold star row + "<total> reseñas sobre Google" (multicolour
     wordmark). Same live data as the footer; gated on the "show score" toggle. */
  function scoreHeaderMarkup(data, settings) {
    if (!settings.showScore) return '';
    var avg = Number(data.averageRating) || 0;
    var avgDot = (Math.round(avg * 10) / 10).toFixed(1);
    var avgComma = avgDot.replace('.', ',');   // es-ES comma decimal, e.g. "4,9"
    var total = Number(data.totalReviewCount) || 0;
    return (
      '<div class="lr-score">' +
      '<div class="lr-score__top">' +
      '<span class="lr-score__num">' + avgComma + '</span>' +
      starsMarkup(avg, avgComma + ' de 5 estrellas') +
      '</div>' +
      '<div class="lr-score__count">' + total + ' reseñas sobre ' + googleWordmark() + '</div>' +
      '</div>'
    );
  }

  function googleReviewsUrl() {
    return GOOGLE_PLACE_ID
      ? 'https://search.google.com/local/reviews?placeid=' + encodeURIComponent(GOOGLE_PLACE_ID)
      : GOOGLE_REVIEW_URL;
  }

  /* Bottom of the section (Sklum-style): gold stars, the exact SEO sentence as
     real selectable text, and the full Google Reviews logo below — the whole
     block links to the Google reviews page. A full-width hairline sits above
     it (CSS). */
  function footerAggregateMarkup(data, settings) {
    var avg = Number(data.averageRating) || 0;
    /* DOT decimal in the footer sentence (e.g. "4.9"), footer only. */
    var avgDot = (Math.round(avg * 10) / 10).toFixed(1);
    /* LIVE true total from the API (not the filtered/text-only subset). */
    var total = Number(data.totalReviewCount) || 0;
    /* Row 1 (rating sentence) + the logo are togglable ("show footer sentence");
       row 2 (the 4-and-5-star note) is ALWAYS shown. avgDot/total numeric → safe. */
    var showSentence = settings.showFooterSentence;
    var row1Html =
      'Valoración de <strong>' + avgDot + '</strong> sobre 5 en base a ' + total +
      ' opiniones en Google.';
    var row2Html = 'Nuestras opiniones de 4 y 5 estrellas.';
    var aria = showSentence
      ? ('Valoración de ' + avgDot + ' sobre 5 en base a ' + total +
         ' opiniones en Google. ' + row2Html)
      : row2Html;
    /* Logo (full Google Reviews logo) only when the sentence is shown, so they
       hide/show together. Width merchant-controlled; height auto keeps 16:9. */
    var lw = settings.logoWidth > 0 ? settings.logoWidth : 150;
    var logo = (showSentence && settings.logoUrl)
      ? '<img class="lr-footer__logo" src="' + esc(settings.logoUrl) +
        '" alt="Reseñas de Google" width="' + lw + '" height="' + Math.round(lw * 0.5625) +
        '" style="width:' + lw + 'px" loading="lazy" decoding="async">'
      : '';
    var rows = '';
    if (showSentence) {
      rows += '<span class="lr-footer__row">' + row1Html + '</span>';
    }
    rows += '<span class="lr-footer__row">' + row2Html + '</span>';
    return (
      '<div class="lr-footer">' +
      '<a class="lr-footer__line" href="' + esc(googleReviewsUrl()) + '" target="_blank" rel="noopener" ' +
      'aria-label="' + esc(aria) + '">' +
      '<span class="lr-footer__text">' + rows + '</span>' +
      logo +
      '</a></div>'
    );
  }

  function ctaMarkup(settings) {
    if (!settings.showCta || !GOOGLE_REVIEW_URL) return '';
    return (
      '<div class="lr-review-cta-wrap">' +
      '<a class="lr-review-cta" href="' + esc(GOOGLE_REVIEW_URL) + '" target="_blank" rel="noopener">' +
      'Dejar una reseña</a>' +
      '</div>'
    );
  }

  function contentMarkup(data, reviews, settings) {
    var cards = reviews
      .map(function (r, i) {
        return '<li class="lr-slide" data-has-media="' + (hasMedia(r) ? 'true' : 'false') + '">' +
          cardMarkup(r, settings, i) + '</li>';
      })
      .join('');

    return (
      headingMarkup(settings) +
      scoreHeaderMarkup(data, settings) +
      '<div class="lr-carousel">' +
      '<button type="button" class="lr-nav lr-nav--prev" aria-label="Reseñas anteriores">' + CHEVRON_LEFT + '</button>' +
      '<ul class="lr-track" role="list" tabindex="0" aria-label="' + esc(settings.heading) + '">' +
      cards +
      '</ul>' +
      '<button type="button" class="lr-nav lr-nav--next" aria-label="Reseñas siguientes">' + CHEVRON_RIGHT + '</button>' +
      '</div>' +
      ctaMarkup(settings) +
      footerAggregateMarkup(data, settings)
    );
  }

  /* ---------- show / hide ---------- */

  function hideSection(root, reason) {
    var wrap = sectionWrapper(root);
    stopAutoplay(root);
    if (isDesignMode()) {
      /* Keep it visible & selectable in the editor with a note. */
      wrap.style.display = '';
      root.removeAttribute('aria-busy');
      root.innerHTML =
        '<div class="lr-placeholder">Esta sección se ocultará en la tienda: ' + esc(reason) + '.</div>';
    } else {
      wrap.style.display = 'none';
      root.setAttribute('aria-hidden', 'true');
    }
  }

  function showSection(root) {
    sectionWrapper(root).style.display = '';
    root.removeAttribute('aria-hidden');
    root.removeAttribute('aria-busy');
  }

  /* ---------- render ---------- */

  function render(root) {
    var settings = readSettings(root);
    root.setAttribute('aria-busy', 'true');

    getData().then(
      function (data) {
        try {
          var reviews = selectReviews(data, settings);
          if (reviews.length < settings.minToShow) {
            hideSection(root, 'no hay suficientes reseñas que cumplan los filtros');
            return;
          }
          root._lrReviews = reviews;
          root.innerHTML = contentMarkup(data, reviews, settings);
          showSection(root);
          bindRoot(root);
          afterRender(root, settings);
        } catch (err) {
          /* A markup/bind error must never leave the section stuck on its
             skeleton: in the editor show a recoverable placeholder, in the
             store hide cleanly. */
          hideSection(root, 'no se pudieron mostrar las reseñas');
        }
      },
      function () {
        hideSection(root, 'la API de reseñas no está disponible');
      }
    );
  }

  /* Reveal "Leer más" only when the comment actually overflows. Measured per
     card when it becomes visible, so it stays correct with content-visibility. */
  function setupClamp(root) {
    var comments = root.querySelectorAll('[data-lr-comment]');
    var measure = function (c) {
      var text = c.querySelector('.lr-card__text');
      var btn = c.parentNode.querySelector('[data-lr-more]');
      if (text && btn && text.scrollHeight - text.clientHeight > 4) btn.hidden = false;
    };
    if (typeof window.IntersectionObserver === 'function') {
      var io = new window.IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) { measure(en.target); io.unobserve(en.target); }
        });
      });
      comments.forEach(function (c) { io.observe(c); });
    } else {
      var raf = window.requestAnimationFrame || function (cb) { return setTimeout(cb, 16); };
      raf(function () { comments.forEach(measure); });
    }
  }

  /* Post-render: clamp detection, carousel. */
  function afterRender(root, settings) {
    setupClamp(root);

    initCarousel(root, settings);
  }

  /* ---------- carousel ---------- */

  function stopAutoplay(root) {
    if (root._lrAutoplay) {
      clearInterval(root._lrAutoplay);
      root._lrAutoplay = null;
    }
  }

  function initCarousel(root, settings) {
    var carousel = root.querySelector('.lr-carousel');
    var track = root.querySelector('.lr-track');
    if (!carousel || !track) return;

    var prev = carousel.querySelector('.lr-nav--prev');
    var next = carousel.querySelector('.lr-nav--next');

    function step() {
      var first = track.querySelector('.lr-slide');
      var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || '0') || 0;
      return first ? first.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
    }

    function overflowing() {
      return track.scrollWidth - track.clientWidth > 2;
    }

    function updateNav() {
      carousel.classList.toggle('has-overflow', overflowing());
      var max = track.scrollWidth - track.clientWidth - 2;
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max;
    }

    function scrollByStep(dir) {
      track.scrollBy({ left: dir * step(), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }

    if (prev) prev.addEventListener('click', function () { scrollByStep(-1); });
    if (next) next.addEventListener('click', function () { scrollByStep(1); });

    track.addEventListener('scroll', updateNav, { passive: true });
    track.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); scrollByStep(1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); scrollByStep(-1); }
    });

    /* Pointer drag (desktop). Touch uses native scroll-snap. */
    var dragging = false, startX = 0, startScroll = 0, moved = false;
    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch') return;
      dragging = true; moved = false;
      startX = e.clientX; startScroll = track.scrollLeft;
      carousel.classList.add('is-dragging');
    });
    track.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = e.clientX - startX;
      if (Math.abs(dx) > 3) moved = true;
      track.scrollLeft = startScroll - dx;
    });
    function endDrag() {
      if (!dragging) return;
      dragging = false;
      carousel.classList.remove('is-dragging');
    }
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('pointerleave', endDrag);
    /* Suppress click (e.g. opening lightbox) at the end of a drag. */
    track.addEventListener('click', function (e) {
      if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
    }, true);

    /* Recompute on resize. */
    if (root._lrResize) window.removeEventListener('resize', root._lrResize);
    root._lrResize = function () { updateNav(); };
    window.addEventListener('resize', root._lrResize, { passive: true });

    updateNav();

    /* Autoplay (opt-in, never under reduced motion). */
    stopAutoplay(root);
    if (settings.autoplay && !prefersReducedMotion() && overflowing()) {
      var advance = function () {
        var max = track.scrollWidth - track.clientWidth - 2;
        if (track.scrollLeft >= max) track.scrollTo({ left: 0, behavior: 'smooth' });
        else scrollByStep(1);
      };
      root._lrAutoplay = setInterval(advance, settings.autoplaySpeed);
      var pause = function () { stopAutoplay(root); };
      var resume = function () {
        if (!root._lrAutoplay && settings.autoplay && !prefersReducedMotion()) {
          root._lrAutoplay = setInterval(advance, settings.autoplaySpeed);
        }
      };
      carousel.addEventListener('pointerenter', pause);
      carousel.addEventListener('pointerleave', resume);
      carousel.addEventListener('focusin', pause);
      carousel.addEventListener('focusout', resume);
    }
  }

  /* ---------- delegated clicks (bound once per root) ---------- */

  function bindRoot(root) {
    if (root._lrBound) return;
    root._lrBound = true;
    root.addEventListener('click', function (e) {
      var more = e.target.closest('[data-lr-more]');
      if (more && root.contains(more)) {
        var card = more.closest('.lr-card');
        if (card) {
          var expanded = card.classList.toggle('is-expanded');
          more.textContent = expanded ? 'Leer menos' : 'Leer más';
        }
        return;
      }
      var lead = e.target.closest('[data-lr-lightbox]');
      if (lead && root.contains(lead)) {
        var idx = parseInt(lead.getAttribute('data-lr-index'), 10) || 0;
        var review = (root._lrReviews || [])[idx];
        if (review) openLightbox((review.media || []).filter(isHttpUrl), 0, lead);
      }
    });
  }

  /* ---------- lightbox ---------- */

  function openLightbox(media, start, trigger) {
    if (!media || !media.length) return;
    var index = start || 0;

    var box = document.createElement('div');
    box.className = 'lr-lightbox';
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');
    box.setAttribute('aria-label', 'Fotos de la reseña');
    box.innerHTML =
      '<button type="button" class="lr-lightbox__close" aria-label="Cerrar">&times;</button>' +
      '<button type="button" class="lr-lightbox__nav lr-lightbox__nav--prev" aria-label="Foto anterior">' + CHEVRON_LEFT + '</button>' +
      '<figure class="lr-lightbox__stage"><img class="lr-lightbox__img" src="' + esc(media[index]) + '" alt="Foto de la reseña"></figure>' +
      '<button type="button" class="lr-lightbox__nav lr-lightbox__nav--next" aria-label="Foto siguiente">' + CHEVRON_RIGHT + '</button>' +
      '<span class="lr-lightbox__counter"></span>';

    var img = box.querySelector('.lr-lightbox__img');
    var counter = box.querySelector('.lr-lightbox__counter');
    var closeBtn = box.querySelector('.lr-lightbox__close');
    var prevBtn = box.querySelector('.lr-lightbox__nav--prev');
    var nextBtn = box.querySelector('.lr-lightbox__nav--next');
    var single = media.length < 2;
    if (single) { prevBtn.style.display = 'none'; nextBtn.style.display = 'none'; }

    function update() {
      img.src = media[index];
      counter.textContent = (index + 1) + ' / ' + media.length;
    }
    function go(d) { index = (index + d + media.length) % media.length; update(); }

    function onKey(e) {
      if (e.key === 'Escape') { close(); }
      else if (e.key === 'ArrowRight' && !single) { go(1); }
      else if (e.key === 'ArrowLeft' && !single) { go(-1); }
      else if (e.key === 'Tab') { trapFocus(e); }
    }
    function trapFocus(e) {
      var f = box.querySelectorAll('button:not([style*="display: none"])');
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    /* Swipe nav on touch. */
    var tx = 0;
    box.addEventListener('touchstart', function (e) { tx = e.changedTouches[0].clientX; }, { passive: true });
    box.addEventListener('touchend', function (e) {
      var dx = e.changedTouches[0].clientX - tx;
      if (!single && Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
    }, { passive: true });

    function close() {
      document.removeEventListener('keydown', onKey);
      document.body.classList.remove('lr-lightbox-open');
      if (box.parentNode) box.parentNode.removeChild(box);
      if (trigger && trigger.focus) trigger.focus();
    }

    closeBtn.addEventListener('click', close);
    prevBtn.addEventListener('click', function () { go(-1); });
    nextBtn.addEventListener('click', function () { go(1); });
    box.addEventListener('click', function (e) { if (e.target === box) close(); });
    document.addEventListener('keydown', onKey);

    document.body.appendChild(box);
    document.body.classList.add('lr-lightbox-open');
    update();
    closeBtn.focus();
  }

  /* ---------- bootstrapping ---------- */

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-lavila-reviews]');
    roots.forEach(function (root) { render(root); });
  }

  if (typeof document !== 'undefined') {
    var boot = function () { initAll(document); };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }

    /* Theme editor: re-render live on setting changes / selection. */
    if (!window.__lavilaReviewsBound) {
      window.__lavilaReviewsBound = true;
      document.addEventListener('shopify:section:load', function (e) {
        initAll(e.target);
      });
      document.addEventListener('shopify:section:select', function (e) {
        var root = e.target.querySelector('[data-lavila-reviews]');
        if (root) render(root);
      });
      document.addEventListener('shopify:section:unload', function (e) {
        var root = e.target.querySelector('[data-lavila-reviews]');
        if (root) {
          stopAutoplay(root);
          if (root._lrResize) { window.removeEventListener('resize', root._lrResize); root._lrResize = null; }
        }
      });
    }
  }

  /* Export pure helpers for Node-based tests (no-op in the browser). */
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      relativeTimeEs: relativeTimeEs,
      selectReviews: selectReviews,
      filledStars: filledStars,
      hasText: hasText,
      hasMedia: hasMedia,
      parseHiddenIds: parseHiddenIds,
      formatAuthor: formatAuthor,
      esc: esc
    };
  }
})();
